import { axios, getConfiguration } from '../../internal';
import { getHelpCenterDataProvider } from './__internal__/helpCenterDataProvider';
const HELP_CENTER_BASE_URL = 'http://documentation.sas.com';
const HELP_CENTER_SERVER_URL = 'https://documentation.sas.com/api/features/sessionFeatureList';
let helpCenterSessionPromise;

/**
 * HelpCenterDataProvider is a contract of the data provider used by helpCenter
 * component. Consumers can implement it to provide custom implementation.
 */

const getSoftwareInfo = async dataProvider => {
  const config = await getConfiguration();
  const softwareInfo = getSoftwareInfoFromConfig(config);
  if (!softwareInfo.softwareVersion && dataProvider.getSoftwareInfo) {
    try {
      softwareInfo.softwareVersion = (await dataProvider.getSoftwareInfo()).softwareVersion;
    } catch {
      // Do nothing
    }
  }
  return softwareInfo;
};
const getHelpCenterSession = async dataProvider => {
  try {
    const enabledFeatures = (await dataProvider.getEnabledFeatures?.()) ?? [];
    // Skip creating a session when there is no feature flag enabled
    if (enabledFeatures.length === 0) return undefined;
    const response = await axios.post(HELP_CENTER_SERVER_URL, JSON.stringify({
      featureFlags: enabledFeatures
    }), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const responseData = response.data;
    if (responseData.requestorId && responseData.expiration) {
      return {
        requestorId: responseData.requestorId,
        expireDate: Date.parse(`${responseData.expiration}Z`) // append "Z" to explicitly specify the UTC timezone
      };
    }
  } catch {
    return;
  }
};
const getHelpCenterSessionId = async dataProvider => {
  const prevSession = await helpCenterSessionPromise;
  if (!helpCenterSessionPromise || prevSession && prevSession.expireDate < Date.now()) {
    helpCenterSessionPromise = getHelpCenterSession(dataProvider);
  }
  const session = await helpCenterSessionPromise;
  return session?.requestorId;
};

/**
 * @returns a Promise which resolved to a url string.
 * The url starts with 'http://documentation.sas.com' and includes the following query parameter pairs:
 * requestor=inapp
 * requestorId=<session_uuid> (if applicable. The session will be created if there are enabled feature flags)
 * softwareVersion (value can be set in the configuration file)
 * softwareSpecialConfig (value can be set in the configuration file)
 * softwareId (value can be set in the configuration file)
 * softwareContextId (value can be set in the configuration file)
 */
export const getHelpCenterUrl = async args => {
  const {
    dataProvider = getHelpCenterDataProvider(),
    invertColors,
    customParams
  } = args ?? {};
  const [softwareInfo, requestorId] = await Promise.all([getSoftwareInfo(dataProvider), getHelpCenterSessionId(dataProvider)]);
  const helpParams = {
    ...softwareInfo,
    requestor: 'inapp',
    requestorId,
    theme: invertColors ? 'hc' : undefined // 'hc' for high contrast
  };

  return buildHelpCenterUrl({
    ...helpParams,
    ...customParams
  });
};
const buildHelpCenterUrl = params => {
  const helpParams = [];
  for (const key of Object.keys(params)) {
    const value = params[key];
    if (value) {
      helpParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return `${HELP_CENTER_BASE_URL}/?${helpParams.join('&')}`;
};
const getSoftwareInfoFromConfig = config => {
  if (config === null) return {};
  const helpConfig = config.get('help');
  return {
    softwareVersion: helpConfig?.softwareVersion,
    softwareSpecialConfig: helpConfig?.softwareSpecialConfig,
    softwareId: helpConfig?.softwareId || config.get('appId'),
    softwareContextId: helpConfig?.softwareContextId || config.get('appId')
  };
};
export const clearHelpCenterSession = () => {
  helpCenterSessionPromise = undefined;
};
//# sourceMappingURL=helpCenter.js.map